import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { skipToken } from "@tanstack/react-query";
import InfiniteScroll from "@v2/components/InfiniteScroll";
import { useModifiersStoreActions } from "@v2/features/modifiers/store/ModifiersStoreContext";
import { useEditorStoreActions } from "@v2/store/editorStore";
import { fileVersionRouter } from "@v2/trpc/routes/fileVersion";
import { History, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { VersionCard, VersionCardSkeleton } from "./VersionCard";

const INITIAL_ITEMS_TO_LOAD = 20;

const VersionHistoryHeader = () => {
  const { setPanelView } = useEditorStoreActions();
  const modifierActions = useModifiersStoreActions();

  return (
    <div className="hidden md:flex flex-row justify-between items-center border-b-0.5 border-border p-4">
      <div className="header-h5">Version History</div>
      <Button
        icon
        onClick={() => {
          modifierActions.setDisplayMode("select");
          setPanelView("chat");
        }}
        variant="tertiary"
        size="sm"
      >
        <X />
      </Button>
    </div>
  );
};

const VersionHistoryContent = () => {
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");

  const {
    data: fileVersionsData,
    fetchNextPage: fetchNextVersionsPage,
    hasNextPage: hasNextVersionsPage,
    isLoading: isLoadingVersions,
  } = fileVersionRouter.findByFileId.useInfiniteQuery(
    pageId
      ? {
          fileId: pageId,
          limit: INITIAL_ITEMS_TO_LOAD,
          groupBy: {
            changeType: "MODIFIER",
          },
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
      staleTime: 0,
    },
  );

  const fileVersions = React.useMemo(
    () => fileVersionsData?.pages?.flatMap((page) => page.fileVersions) ?? [],
    [fileVersionsData],
  );

  if (fileVersions.length === 0 && !isLoadingVersions) {
    return (
      <div className="flex justify-center items-center h-full">
        <Empty
          title="No versions yet"
          description="Version history will appear here as you make changes to your page."
          icon={<History />}
        />
      </div>
    );
  }

  if (isLoadingVersions) {
    return (
      <div className="flex flex-col gap-3 overflow-y-auto flex-1 md:p-4">
        {Array.from({ length: INITIAL_ITEMS_TO_LOAD }).map((_, index) => (
          <VersionCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 overflow-y-auto flex-1 md:p-4"
      id="versions-scroll-container"
    >
      <InfiniteScroll
        dataLength={fileVersions.length}
        next={() => void fetchNextVersionsPage()}
        hasMore={hasNextVersionsPage}
        scrollableTarget="versions-scroll-container"
        loader={
          <div className="flex flex-col gap-3 overflow-y-auto flex-1 mt-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <VersionCardSkeleton key={index} />
            ))}
          </div>
        }
      >
        <div className="flex flex-col gap-3 w-full items-center">
          {fileVersions?.map((version) => (
            <VersionCard
              key={version.id}
              version={version}
              className="w-full"
              currentPagePreviewVersionClassName="my-1"
            />
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
};

function VersionHistory() {
  return (
    <div className="flex flex-col w-full h-full md:shadow-2xs md:border-0.5 md:border-border md:rounded-lg bg-white relative overflow-hidden">
      <VersionHistoryHeader />
      <VersionHistoryContent />
    </div>
  );
}

export default VersionHistory;
